"use client";

import { useState, useEffect } from "react";
import { Phone, Mail, Calendar, ArrowUp, MessageCircle } from "lucide-react";
import BookingModal from "./BookingModal";
import { PHONE_NUMBER_TEL_LINK, WHATSAPP_LINK } from "@/constants/contact";

export default function FloatingSidebar() {
	const [showScrollTop, setShowScrollTop] = useState(false);
	const [isVisible, setIsVisible] = useState(true);
	const [lastScrollY, setLastScrollY] = useState(0);

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			setShowScrollTop(currentScrollY > 300);

			// Hide/show sidebar based on scroll direction
			if (currentScrollY > lastScrollY && currentScrollY > 100) {
				setIsVisible(false); // Scrolling down
			} else {
				setIsVisible(true); // Scrolling up
			}

			setLastScrollY(currentScrollY);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [lastScrollY]);

	const scrollToTop = () => {
		// Track GA4 event
		if (typeof (window as any).gtag === "function") {
			(window as any).gtag("event", "scroll_to_top", {
				event_category: "engagement",
				event_label: "Floating Sidebar",
			});
		}

		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	};

	const handlePhoneClick = () => {
		// Track GA4 event
		if (typeof (window as any).gtag === "function") {
			(window as any).gtag("event", "phone_click", {
				event_category: "contact",
				event_label: "Floating Sidebar - Phone",
				phone_number: "(01) 813 31 23",
			});
		}

		window.location.href = PHONE_NUMBER_TEL_LINK;
	};

	const handleEmailClick = () => {
		// Track GA4 event
		if (typeof (window as any).gtag === "function") {
			(window as any).gtag("event", "email_click", {
				event_category: "contact",
				event_label: "Floating Sidebar - Email",
				email: "info@stelladent.at",
			});
		}

		window.location.href = "mailto:info@stelladent.at";
	};

	const handleWhatsAppClick = () => {
		// Track GA4 event
		if (typeof (window as any).gtag === "function") {
			(window as any).gtag("event", "whatsapp_click", {
				event_category: "contact",
				event_label: "Floating Sidebar - WhatsApp",
			});
		}

		window.open(WHATSAPP_LINK, "_blank");
	};

	return (
		<>
			{/* Floating Action Buttons */}
			<div
				className={`fixed right-6 top-1/2 -translate-y-1/2 z-50 flex-col space-y-3 hidden lg:flex transition-all duration-300 ${
					isVisible
						? "translate-x-0 opacity-100"
						: "translate-x-20 opacity-0"
				}`}
				suppressHydrationWarning={true}
			>
				{/* Phone Button */}
				<button
					onClick={handlePhoneClick}
					className="group relative w-14 h-14 bg-green-600 hover:bg-green-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer"
					title="Anrufen"
				>
					<Phone className="w-6 h-6" />
					<div className="absolute right-16 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
						(01) 813 31 23
						<div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
					</div>
				</button>

				{/* Email Button */}
				<button
					onClick={handleEmailClick}
					className="group relative w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer"
					title="E-Mail senden"
				>
					<Mail className="w-6 h-6" />
					<div className="absolute right-16 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
						E-Mail senden
						<div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
					</div>
				</button>

				{/* WhatsApp Button */}
				<button
					onClick={handleWhatsAppClick}
					className="group relative w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer"
					title="WhatsApp"
				>
					<MessageCircle className="w-6 h-6" />
					<div className="absolute right-16 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
						WhatsApp
						<div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
					</div>
				</button>

				{/* Booking Button */}
				<BookingModal>
					<button className="group relative w-14 h-14 bg-[#4d111f] hover:bg-[#4d111f]/80 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer border-2 border-[#4d111f]/30">
						<Calendar className="w-6 h-6" />
						<div className="absolute right-16 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
							Termin buchen
							<div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
						</div>
					</button>
				</BookingModal>
			</div>

			{/* Scroll to Top Button */}
			{showScrollTop && (
				<button
					onClick={scrollToTop}
					className="fixed right-6 bottom-6 z-50 w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer border-2 border-slate-500/30"
					title="Nach oben scrollen"
				>
					<ArrowUp className="w-6 h-6" />
				</button>
			)}
		</>
	);
}
